/**
 * プレミアムゲート管理
 * AI採点の使用回数を管理する。
 * - ゲスト：端末ごとに生涯5回まで（日付リセットなし。ログインすると別カウンタに切り替わる）
 * - 無料ログイン：1日2回
 * - プレミアム：1日10回
 */

const AI_SCORE_KEY         = 'ai_score_usage';          // { date, count }（ログイン済み無料・プレミアム用）
const GUEST_LIFETIME_KEY   = 'ai_score_guest_lifetime';  // number（ゲストの生涯合計・日付リセットなし）

const FREE_LIMIT           = 2;   // ログイン済み無料：1日2回
const PREMIUM_LIMIT        = 10;  // プレミアム：1日10回
const GUEST_LIFETIME_LIMIT = 5;   // ゲスト：生涯5回

function getTodayStr() {
  return new Date().toISOString().slice(0, 10); // 'YYYY-MM-DD'
}

/** 今日の使用回数を取得（ログイン済みユーザー用） */
export function getAIScoreUsage() {
  try {
    const raw = JSON.parse(localStorage.getItem(AI_SCORE_KEY) || '{}');
    if (raw.date === getTodayStr()) return raw.count ?? 0;
    return 0; // 日付が変わっていたらリセット
  } catch { return 0; }
}

/** ゲストの生涯使用回数を取得（日付リセットなし） */
export function getGuestLifetimeUsage() {
  try { return parseInt(localStorage.getItem(GUEST_LIFETIME_KEY) || '0', 10) || 0; } catch { return 0; }
}

/** 使用回数を1増やす（ゲストは生涯カウンタ、それ以外は日次カウンタ） */
export function incrementAIScoreUsage(isGuest = false) {
  if (isGuest) {
    localStorage.setItem(GUEST_LIFETIME_KEY, String(getGuestLifetimeUsage() + 1));
    return;
  }
  const count = getAIScoreUsage() + 1;
  localStorage.setItem(AI_SCORE_KEY, JSON.stringify({ date: getTodayStr(), count }));
}

/** 上限を取得 */
export function getAIScoreLimit(isPremium, isGuest = false) {
  if (isGuest) return GUEST_LIFETIME_LIMIT;
  return isPremium ? PREMIUM_LIMIT : FREE_LIMIT;
}

/** AI採点が使えるか */
export function canUseAIScore(isPremium, isGuest = false) {
  const used = isGuest ? getGuestLifetimeUsage() : getAIScoreUsage();
  return used < getAIScoreLimit(isPremium, isGuest);
}

/** 残り回数 */
export function getRemainingAIScore(isPremium, isGuest = false) {
  const used = isGuest ? getGuestLifetimeUsage() : getAIScoreUsage();
  return Math.max(0, getAIScoreLimit(isPremium, isGuest) - used);
}

/**
 * AI採点が使えるか（日次/生涯制限 OR クレジット残あり）
 * credits: Supabase の ai_score_credits（⚠️ ゲストは購入にログインが必要なので常に0扱い）
 */
export function canUseAIScoreOrCredit(isPremium, credits = 0, isGuest = false) {
  return canUseAIScore(isPremium, isGuest) || credits > 0;
}

/** 日次/生涯の上限は超えているが、クレジットで使えるか */
export function shouldUseCredit(isPremium, credits = 0, isGuest = false) {
  return !canUseAIScore(isPremium, isGuest) && credits > 0;
}

// ── 写真OCR 日次制限 ────────────────────────────────
const PHOTO_KEY        = 'photo_usage';
const PHOTO_FREE_LIMIT = 3;

export function getPhotoUsage() {
  try {
    const raw = JSON.parse(localStorage.getItem(PHOTO_KEY) || '{}');
    if (raw.date === getTodayStr()) return raw.count ?? 0;
    return 0;
  } catch { return 0; }
}

export function incrementPhotoUsage() {
  const count = getPhotoUsage() + 1;
  localStorage.setItem(PHOTO_KEY, JSON.stringify({ date: getTodayStr(), count }));
}

/** プレミアムは無制限、無料ログインは1日3枚まで */
export function canUsePhoto(isPremium) {
  if (isPremium) return true;
  return getPhotoUsage() < PHOTO_FREE_LIMIT;
}

export function getRemainingPhoto() {
  return Math.max(0, PHOTO_FREE_LIMIT - getPhotoUsage());
}
